import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { useWeb3Wallet } from "shared/lib/hooks/useWeb3Wallet";
import { AnimatePresence, motion } from "framer";

import {
  getAssets,
  isNativeToken,
  VaultAddressMap,
  VaultAllowedDepositAssets,
  VaultOptions,
  VaultVersion,
  Chains,
  CHAINS_TO_ID
} from "shared/lib/constants/constants";

import { useWeb3Context } from "shared/lib/hooks/web3Context";
import { getVaultColor } from "shared/lib/utils/vault";
import { getERC20Token } from "shared/lib/hooks/useERC20Token";
import { ERC20Token } from "shared/lib/models/eth";
import useLoadingText from "shared/lib/hooks/useLoadingText";
import {
  // BaseLink,
  PrimaryText,
  // SecondaryText,
  Title,
} from "shared/lib/designSystem";
import colors from "shared/lib/designSystem/colors";
import { usePendingTransactions } from "shared/lib/hooks/pendingTransactionsContext";
import { ActionButton } from "shared/lib/components/Common/buttons";
import {
  getAssetColor,
  getAssetDisplay,
  getAssetLogo,
} from "shared/lib/utils/asset";
import useVaultActionForm from "../../../../hooks/useVaultActionForm";
import ButtonArrow from "shared/lib/components/Common/ButtonArrow";
import theme from "shared/lib/designSystem/theme";

import { useWeb3React } from "@web3-react/core";
import { switchChains } from "shared/lib/utils/chainSwitching";
import useConnectWalletModal from "shared/lib/hooks/useConnectWalletModal";


const ApprovalIconContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 16px 0;
`;

const ApprovalIcon = styled.div<{ color: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 64px;
  height: 64px;
  border-radius: 100px;
  background-color: ${(props) => props.color}29;
`;

const DepositAssetSwitchContainer = styled.div`
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
  background: ${colors.background.three};
  border-radius: 100px;
  padding: 8px;
`;

const DepositAssetSwitchContainerLogo = styled.div<{
  color: string;
}>`
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
  height: 48px;
  width: 48px;
  border-radius: 100px;
  background: ${colors.background.one};

  &:before {
    position: absolute;
    content: " ";
    width: 100%;
    height: 100%;
    background: ${(props) => `${props.color}14`};
    border-radius: 100px;
  }
`;

const DepositAssetsSwitchDropdown = styled(motion.div)<{
  isOpen: boolean;
}>`
  ${(props) =>
    props.isOpen
      ? `
          position: absolute;
          z-index: 2000;
          padding: 8px;

          width: fit-content;
          background-color: ${colors.background.three};
          border-radius: ${theme.border.radius};
          top: 72px;
        `
      : `
          display: none;
        `}
`;

const DepositAssetsSwitchDropdownItem = styled.div<{
  color: string;
  active: boolean;
}>`
  display: flex;
  align-items: center;
  padding: 8px;
  opacity: 0.48;
  border-radius: 100px;
  background: ${(props) => `${props.color}14`};
  margin-bottom: 8px;
  border: ${theme.border.width} ${theme.border.style} transparent;
  transition: border 150ms;

  &:last-child {
    margin-bottom: 0px;
  }

  ${(props) => {
    if (props.active) {
      return `
        opacity: 1;
        border: ${theme.border.width} ${theme.border.style} ${props.color};
      `;
    }
    return `
      &:hover {
        opacity: 1;
      }
    `;
  }}
`;

const ApprovalDescription = styled(PrimaryText)`
  display: block;
  text-align: center;
  margin-bottom: 16px;
`;

// const ApprovalHelp = styled(BaseLink)`
//   display: flex;
//   justify-content: center;
//   text-decoration: underline ${colors.text};
//   margin-bottom: 40px;

//   &:hover {
//     text-decoration: underline ${colors.text}A3;

//     span {
//       color: ${colors.text}A3;
//     }
//   }
// `;

interface VaultApprovalFormProps {
  vaultOption: VaultOptions;
  version: VaultVersion;
  showDepositAssetSwap?: boolean;
}

const VaultApprovalForm: React.FC<VaultApprovalFormProps> = ({
  vaultOption,
  version,
  showDepositAssetSwap = false,
}) => {
  const { vaultActionForm, handleDepositAssetChange } =
    useVaultActionForm(vaultOption);
  const asset = getAssets(vaultOption);
  const color = getVaultColor(vaultOption);
  const [depositAssetMenuOpen, setDepositAssetMenuOpen] = useState(false);

  const { chainId, ethereumProvider } = useWeb3Wallet();
  const { provider } = useWeb3Context();
  const { addPendingTransaction } = usePendingTransactions();

  const {chainId: chainId2, 
    // active : active2, 
    // error, 
    // activate, 
    // setError, 
    library, 
    // account: account2 
  } = useWeb3React();
  const [, setShowConnectModal] = useConnectWalletModal();
  const {
    active,
    account,
  } = useWeb3Wallet();

  const depositAsset = useMemo(() => {
    switch (version) {
      case "v1":
        return asset;
      default:
        return (
          vaultActionForm.depositAsset ||
          VaultAllowedDepositAssets[vaultOption][0]
        );
    }
  }, [asset, vaultActionForm.depositAsset, vaultOption, version]);
  const Logo = getAssetLogo(depositAsset);

  const tokenContract = useMemo(() => {
    if (isNativeToken(depositAsset) || !chainId) {
      return;
    }

    return getERC20Token(
      ethereumProvider,
      depositAsset.toLowerCase() as ERC20Token,
      chainId
    );
  }, [chainId, depositAsset, ethereumProvider]);

  const [waitingApproval, setWaitingApproval] = useState(false);
  const loadingText = useLoadingText("Approving");

  const handleApproveToken = useCallback(async () => {
    const approveToAddress = VaultAddressMap[vaultOption][version];
    // console.log('---------0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff----------');
    // console.log(tokenContract);
    // console.log(approveToAddress);

    if (tokenContract && approveToAddress) {
      setWaitingApproval(true);
      const amount =
        "0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff";
      try {
        const tx = await tokenContract.approve(approveToAddress, amount);

        const txhash = tx.hash;

        addPendingTransaction({
          txhash,
          type: "approval",
          amount: amount,
          vault: vaultOption,
          asset: depositAsset,
        });

        // Wait for transaction to be approved
        await provider.waitForTransaction(txhash, 2);
      } catch (err) {
      } finally {
        setWaitingApproval(false);
      }
    }
  }, [
    addPendingTransaction,
    depositAsset,
    provider,
    tokenContract,
    vaultOption,
    version,
  ]);

  const networkStatus = useMemo(() => {
    let networkChainId:any;
    try{
      networkChainId = chainId2;
    } catch(e) {
      console.log('error:', e)
    }
    if(networkChainId !== CHAINS_TO_ID[Chains.BSC] && networkChainId !== undefined){
      return false;
    }
    return true;
  }, [chainId2])

  const handleButtonClick = useCallback(async () => {
    let networkChainId:any;
    try{
      networkChainId = chainId2;
    } catch(e) {
      console.log('error:', e)
    }
    if(networkChainId !== CHAINS_TO_ID[Chains.BSC] && networkChainId !== undefined){
      if(chainId !== CHAINS_TO_ID[Chains.BSC] && chainId !== undefined){
        // if(ethereumProvider != undefined){
          await switchChains(library, CHAINS_TO_ID[Chains.BSC]);
        // }
        return;
      }
      setShowConnectModal(true);
    } else {
      if (!active || ! account) {
        setShowConnectModal(true);
      }
    }
    
  }, [chainId2, chainId, active, account, setShowConnectModal, library]);


  return (
    <>
      <ApprovalIconContainer>
        {showDepositAssetSwap ? (
          <DepositAssetSwitchContainer
            role="button"
            onClick={() => setDepositAssetMenuOpen((show) => !show)}
          >
            <DepositAssetSwitchContainerLogo color={color}>
              <Logo height={40} width={40} />
            </DepositAssetSwitchContainerLogo>
            <Title className="mx-2">{getAssetDisplay(depositAsset)}</Title>
            <ButtonArrow
              isOpen={depositAssetMenuOpen}
              className="mr-2"
              color={colors.primaryText}
            />
            <AnimatePresence>
              <DepositAssetsSwitchDropdown
                key={depositAssetMenuOpen.toString()}
                isOpen={depositAssetMenuOpen}
                initial={{
                  opacity: 0,
                  y: 20,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                exit={{
                  opacity: 0,
                  y: 20,
                }}
                transition={{
                  type: "keyframes",
                  duration: 0.2,
                }}
              >
                {VaultAllowedDepositAssets[vaultOption].map((assetOption) => {
                  const Logo = getAssetLogo(assetOption);
                  return (
                    <DepositAssetsSwitchDropdownItem
                      color={getAssetColor(assetOption)}
                      active={assetOption === depositAsset}
                      onClick={() => handleDepositAssetChange(assetOption)}
                    >
                      <DepositAssetSwitchContainerLogo
                        color={getAssetColor(assetOption)}
                      >
                        <Logo height={40} width={40} />
                      </DepositAssetSwitchContainerLogo>
                      <Title className="ml-2 mr-4">
                        {getAssetDisplay(assetOption)}
                      </Title>
                    </DepositAssetsSwitchDropdownItem>
                  );
                })}
              </DepositAssetsSwitchDropdown>
            </AnimatePresence>
          </DepositAssetSwitchContainer>
        ) : (
          <ApprovalIcon color={color}>
            <Logo />
          </ApprovalIcon>
        )}
      </ApprovalIconContainer>
      {
        networkStatus ? <ApprovalDescription>
          Before you deposit, the vault needs your permission to invest your{" "}
          {getAssetDisplay(depositAsset)} in the vault’s strategy.
        </ApprovalDescription> : <ApprovalDescription> </ApprovalDescription>
      }
      
      {/* <ApprovalHelp
        to="https://docs.ribbon.finance/faq"
        target="__blank"
        rel="noreferrer noopener"
      >
        <SecondaryText>Why do I have to do this?</SecondaryText>
      </ApprovalHelp> */}
      
      {
        networkStatus ? <ActionButton
          onClick={handleApproveToken}
          className="py-3 mb-4"
          color={color}
        >
          {waitingApproval
            ? loadingText
            : `Approve ${getAssetDisplay(depositAsset)}`}
        </ActionButton> : <ActionButton
          className="py-3 mb-4"
          color="#ffffff"
          onClick={handleButtonClick}
        >
          Change Network
        </ActionButton>
      }
      
    </>
  );
};

export default VaultApprovalForm;
